/**
 * egress.service.js — Scan network (egress) settings for an organization.
 * Which IPs scans come from (for customer allow-lists) and how scans reach the site.
 */
import { requestJson } from './_core';

const orgQuery = (orgId) => {
  const id = String(orgId || '').trim();
  return id ? { org_id: id } : null;
};

export function getEgressSettings(orgId = '', token = null) {
  return requestJson('GET', '/egress/settings/', null, token, orgQuery(orgId));
}

export function updateEgressSettings(orgId = '', payload = {}, token = null) {
  return requestJson('PATCH', '/egress/settings/', payload, token, orgQuery(orgId));
}

/** Read-only route choices for the scan manager (any user who can scan for the organization). */
export function getEgressScanOptions(orgId = '', token = null) {
  return requestJson('GET', '/egress/scan-options/', null, token, orgQuery(orgId));
}

// "Your own proxy": profiles, connection tests and per-site settings.
export function listEgressProxies(orgId = '', token = null) {
  return requestJson('GET', '/egress/proxies/', null, token, orgQuery(orgId));
}

export function createEgressProxy(orgId = '', payload = {}, token = null) {
  return requestJson('POST', '/egress/proxies/', payload, token, orgQuery(orgId));
}

export function updateEgressProxy(proxyId, payload = {}, token = null) {
  return requestJson('PATCH', `/egress/proxies/${encodeURIComponent(proxyId)}/`, payload, token);
}

export function deleteEgressProxy(proxyId, token = null) {
  return requestJson('DELETE', `/egress/proxies/${encodeURIComponent(proxyId)}/`, null, token);
}

/** Start a connection test: { proxy: id } for a saved proxy, or { scheme, host, port, username, password }. */
export function startEgressProxyTest(orgId = '', payload = {}, token = null) {
  return requestJson('POST', '/egress/proxies/test/', payload, token, orgQuery(orgId));
}

export function getEgressProxyTest(testId, token = null) {
  return requestJson('GET', `/egress/proxy-tests/${encodeURIComponent(testId)}/`, null, token);
}

export function getSiteEgressSetting(siteId, token = null) {
  return requestJson('GET', `/egress/sites/${encodeURIComponent(siteId)}/`, null, token);
}

export function updateSiteEgressSetting(siteId, payload = {}, token = null) {
  return requestJson('PUT', `/egress/sites/${encodeURIComponent(siteId)}/`, payload, token);
}

// Staff console (xSPECTRE staff; changes need a global admin, enforced by the API).
export function getEgressStaffOverview(token = null) {
  return requestJson('GET', '/egress/staff/overview/', null, token);
}

export function getEgressStaffVendorUsage(token = null) {
  return requestJson('GET', '/egress/staff/vendor-usage/', null, token);
}

export function getEgressStaffScans(params = {}, token = null) {
  return requestJson('GET', '/egress/staff/scans/', null, token, params);
}

export function getEgressStaffAudit(params = {}, token = null) {
  return requestJson('GET', '/egress/staff/audit/', null, token, params);
}

export function startEgressStaffProxyTest(proxyId, token = null) {
  return requestJson('POST', `/egress/staff/proxies/${encodeURIComponent(proxyId)}/test/`, {}, token);
}

export function getEgressStaffProxyTest(testId, token = null) {
  return requestJson('GET', `/egress/staff/proxy-tests/${encodeURIComponent(testId)}/`, null, token);
}

export function setEgressStaffProxyStatus(proxyId, payload = {}, token = null) {
  return requestJson('POST', `/egress/staff/proxies/${encodeURIComponent(proxyId)}/status/`, payload, token);
}

export function retryEgressStaffIp(ipId, token = null) {
  return requestJson('POST', `/egress/staff/ips/${encodeURIComponent(ipId)}/retry/`, {}, token);
}

/** Buys an IP: { pool, country, organization?, confirm: 'ORDER' }. */
export function orderEgressStaffIp(payload = {}, token = null) {
  return requestJson('POST', '/egress/staff/ips/order/', payload, token);
}

export function reconcileEgressStaffDedicated(orgId, token = null) {
  return requestJson('POST', `/egress/staff/dedicated/${encodeURIComponent(orgId)}/reconcile/`, {}, token);
}
